import NewsletterAPI from '../../../../api'
import { GetLanguageStatistics } from 'aldoo-ra/Localization/api'

/**
 * AudienceController
 * Handles all audience-related data operations
 */
class AudienceController {
  /**
   * Load available segments
   */
  async loadSegments() {
    try {
      const response = await NewsletterAPI.getSegments()
      if (response && response.success && response.data) {
        return { success: true, segments: response.data || [] }
      }
      return { success: false, segments: [] }
    } catch (error) {
      console.error('Failed to load segments:', error)
      return { success: false, segments: [], error: error.message }
    }
  }

  /**
   * Load system languages
   */
  async loadSystemLanguages() {
    try {
      const response = await GetLanguageStatistics()
      if (response && response.languages) {
        const languages = [
          { value: "any", label: "Any Language" },
          ...response.languages.map(lang => ({
            value: lang.code,
            label: lang.name
          }))
        ]
        return { success: true, languages }
      }
      return { success: false, languages: this.getFallbackLanguages() }
    } catch (error) {
      console.error('Failed to load system languages:', error)
      return { success: false, languages: this.getFallbackLanguages(), error: error.message }
    }
  }

  /**
   * Fallback languages if API fails
   */
  getFallbackLanguages() {
    return [
      { value: "any", label: "Any Language" },
      { value: "en", label: "English" },
      { value: "es", label: "Spanish" },
      { value: "fr", label: "French" },
      { value: "de", label: "German" }
    ]
  }

  /**
   * Preview audience based on filters
   */
  async previewAudience(segments, includeUnregistered, targetLanguages, targetPlatforms, ltvAmount, ltvOperator, deliveryOperator, deliveryReleases, audienceShare) {
    try {
      const filteredLanguages = targetLanguages.includes('any') ? [] : targetLanguages
      const filteredPlatforms = targetPlatforms.includes('any') ? [] : targetPlatforms

      const response = await NewsletterAPI.previewAudience(
        segments,
        includeUnregistered,
        filteredLanguages,
        filteredPlatforms,
        ltvAmount,
        ltvOperator,
        deliveryOperator,
        deliveryReleases,
        audienceShare || 100
      )

      if (response.success || response.audienceSize !== undefined) {
        return { success: true, preview: response }
      }
      return { success: false, preview: null }
    } catch (error) {
      console.error('Failed to preview audience:', error)
      return { success: false, preview: null, error: error.message }
    }
  }

  /**
   * Find default segment (all/subscribers)
   */
  findDefaultSegment(segments) {
    if (!segments || segments.length === 0) return null

    const allSegment = segments.find(seg =>
      seg.name?.toLowerCase().includes('all') ||
      seg.name?.toLowerCase().includes('subscriber') ||
      seg.name?.toLowerCase().includes('everyone')
    )

    return allSegment || segments[0]
  }

  /**
   * Handle "Any" exclusivity for multi-select
   */
  handleAnyExclusivity(selectedValues, currentValues) {
    const hasAny = selectedValues.includes('any')
    const hasSpecific = selectedValues.some(code => code !== 'any')

    if (hasAny && hasSpecific) {
      const wasAnySelected = currentValues.includes('any')

      if (wasAnySelected) {
        return selectedValues.filter(code => code !== 'any')
      } else {
        return ['any']
      }
    } else if (selectedValues.length === 0) {
      return ['any']
    }

    return selectedValues
  }
}

export default new AudienceController()
